import type { PDFFile, Project, EventCallback, TextSelection, ProjectDocument } from '@/types'

export class AppState {
  private state: {
    currentProject: Project | null
    currentFile: PDFFile | null
    currentStep: number
    selections: TextSelection[]
    projectDocuments: ProjectDocument[]
    isLoading: boolean
    error: string | null
    descriptionList: string | null
    coordinates: string[]
    hasFormattedText: boolean
    advancedMode: boolean
    ocrStatuses: Map<string, any>
  } = {
    currentProject: null,
    currentFile: null,
    currentStep: 1,
    selections: [],
    projectDocuments: [],
    isLoading: false,
    error: null,
    descriptionList: null,
    coordinates: [],
    hasFormattedText: false,
    advancedMode: true,
    ocrStatuses: new Map()
  }

  private subscribers: Map<string, EventCallback[]> = new Map()
  
  // Request queue management to prevent concurrent requests
  private requestQueue: Map<string, Promise<any>> = new Map()

  constructor() {
    console.log('📊 AppState initialized')
  }

  // Request queue methods for preventing concurrent operations
  async enqueueRequest<T>(key: string, request: () => Promise<T>): Promise<T> {
    // Cancel any existing request with the same key
    if (this.requestQueue.has(key)) {
      console.log(`🔄 Cancelling existing request: ${key}`)
      // Note: We can't actually cancel the promise, but we can ignore its result
    }

    const promise = this.executeWithTimeout(request, 10000, `Request ${key} timed out`)
    this.requestQueue.set(key, promise)

    try {
      const result = await promise
      return result
    } catch (error) {
      console.error(`❌ Request failed: ${key}`, error)
      throw error
    } finally {
      this.requestQueue.delete(key)
    }
  }

  private async executeWithTimeout<T>(
    promise: () => Promise<T>, 
    timeout: number, 
    errorMessage: string
  ): Promise<T> {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(errorMessage)), timeout)
    })

    return Promise.race([promise(), timeoutPromise])
  }

  // Clear all pending requests (for cleanup during project switches)
  clearPendingRequests(): void {
    console.log(`🧹 Clearing ${this.requestQueue.size} pending requests`)
    this.requestQueue.clear()
  }

  // Check if any requests are pending
  hasPendingRequests(): boolean {
    return this.requestQueue.size > 0
  }

  // Wait for all pending requests to complete
  async waitForPendingRequests(timeout: number = 5000): Promise<void> {
    if (this.requestQueue.size === 0) return

    console.log(`⏳ Waiting for ${this.requestQueue.size} pending requests...`)
    
    const allPromises = Array.from(this.requestQueue.values())
    const timeoutPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        console.log('⚠️ Timeout waiting for pending requests')
        resolve()
      }, timeout)
    })

    try {
      await Promise.race([
        Promise.allSettled(allPromises),
        timeoutPromise
      ])
    } catch (error) {
      console.error('❌ Error waiting for pending requests:', error)
    }
  }

  // Subscribe to state changes
  subscribe<T = any>(key: string, callback: EventCallback<T>): void {
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, [])
    }
    this.subscribers.get(key)!.push(callback)
  }

  // Emit state changes to subscribers
  private emit(key: string, data: any): void {
    const callbacks = this.subscribers.get(key)
    if (callbacks) {
      callbacks.forEach(callback => callback(data))
    }
  }

  // Enhanced project switching with proper request management
  async setCurrentProject(project: Project | null, force: boolean = false): Promise<void> {
    const oldProject = this.state.currentProject
    const oldDocumentCount = oldProject?.document_count || 0
    const newDocumentCount = project?.document_count || 0
    
    if (!force && this.state.currentProject?.id === project?.id) {
      // Even if same project, check if document_count changed
      if (oldDocumentCount !== newDocumentCount) {
        console.log('🔄 AppState - Same project but document count changed:', {
          projectId: project?.id,
          oldDocumentCount,
          newDocumentCount
        })
        this.state.currentProject = project
        this.emit('currentProject', project)
      }
      return // No change needed
    }

    console.log('🔄 AppState - Setting current project:', {
      oldProjectId: oldProject?.id || 'None',
      oldDocumentCount,
      newProjectId: project?.id || 'None',
      newDocumentCount,
      force
    })

    try {
      // Wait for any pending requests to complete before switching
      await this.waitForPendingRequests()
      
      // Clear any remaining requests
      this.clearPendingRequests()

      this.state.currentProject = project
      this.emit('currentProject', project)
      
      console.log('🔄 AppState - Project updated and event emitted')
    } catch (error) {
      console.error('❌ AppState - Failed to switch project:', error)
      // Emit error event for UI handling
      this.emit('error', `Failed to switch project: ${error instanceof Error ? error.message : String(error)}`)
      throw error
    }
  }

  // Add error recovery method
  async recoverFromError(): Promise<void> {
    console.log('🔄 AppState - Recovering from error state...')
    
    try {
      // Clear any stuck requests
      this.clearPendingRequests()
      
      // Reset loading states
      this.state.isLoading = false
      this.state.error = null
      
      // Emit recovery event
      this.emit('recovery', true)
      
      console.log('✅ AppState - Recovery completed')
    } catch (error) {
      console.error('❌ AppState - Recovery failed:', error)
      this.state.error = error instanceof Error ? error.message : String(error)
      this.emit('error', this.state.error)
    }
  }

  // Add health check method
  async checkHealth(): Promise<boolean> {
    try {
      const currentProject = this.state.currentProject
      
      // Basic health checks
      const hasValidProject = currentProject !== null
      const hasNoPendingRequests = this.requestQueue.size === 0
      const hasNoErrors = this.state.error === null
      
      const isHealthy = hasValidProject && hasNoPendingRequests && hasNoErrors
      
      console.log('🔍 AppState - Health check:', {
        hasValidProject,
        hasNoPendingRequests,
        hasNoErrors,
        isHealthy
      })
      
      return isHealthy
    } catch (error) {
      console.error('❌ AppState - Health check failed:', error)
      return false
    }
  }

  // Getters
  getCurrentProject(): Project | null {
    return this.state.currentProject
  }

  getCurrentFile(): PDFFile | null {
    return this.state.currentFile
  }

  getCurrentStep(): number {
    return this.state.currentStep
  }

  getSelections(): TextSelection[] {
    return this.state.selections
  }

  getProjectDocuments(): ProjectDocument[] {
    return this.state.projectDocuments
  }

  // New method for generic documents (including manual text files)
  getDocuments(): any[] {
    return this.state.projectDocuments
  }

  setDocuments(documents: any[]): void {
    this.state.projectDocuments = documents
    this.emit('projectDocuments', documents)
    console.log('📂 Documents updated:', documents.length)
  }

  getIsLoading(): boolean {
    return this.state.isLoading
  }

  getError(): string | null {
    return this.state.error
  }

  getDescriptionList(): string | null {
    return this.state.descriptionList
  }

  getCoordinates(): string[] {
    console.log('📍 Getting coordinates:', this.state.coordinates.length, 'items')
    return this.state.coordinates
  }

  getHasFormattedText(): boolean {
    return this.state.hasFormattedText
  }

  // Setters
  setCurrentFile(file: PDFFile | null): void {
    this.state.currentFile = file
    this.emit('currentFile', file)
    console.log('📄 Current file updated:', file?.filename || 'None')
  }

  setCurrentStep(step: number): void {
    this.state.currentStep = step
    this.emit('currentStep', step)
    console.log('🚀 Current step updated:', step)
  }

  setLoading(loading: boolean): void {
    this.state.isLoading = loading
    this.emit('isLoading', loading)
  }

  setError(error: string | null): void {
    this.state.error = error
    this.emit('error', error)
  }

  // Enhanced selection management with request queuing
  async setSelections(selections: TextSelection[]): Promise<void> {
    return this.enqueueRequest('setSelections', async () => {
      this.state.selections = selections
      this.emit('selections', selections)
      console.log('✂️ Selections updated:', selections.length)
    })
  }

  async addSelection(selection: TextSelection): Promise<void> {
    return this.enqueueRequest('addSelection', async () => {
      const existingIndex = this.state.selections.findIndex(s => s.id === selection.id)
      if (existingIndex !== -1) {
        this.state.selections[existingIndex] = selection
        console.log('🔄 Selection updated:', selection.id)
      } else {
        this.state.selections.push(selection)
        console.log('➕ Selection added:', selection.id)
      }
      console.log('📊 Total selections now:', this.state.selections.length)
      this.emit('selections', this.state.selections)
      console.log('📡 Emitted selections event with', this.state.selections.length, 'selections')
    })
  }

  async removeSelection(selectionId: string): Promise<void> {
    return this.enqueueRequest('removeSelection', async () => {
      this.state.selections = this.state.selections.filter(s => s.id !== selectionId)
      this.emit('selections', this.state.selections)
      console.log('➖ Selection removed:', selectionId)
    })
  }

  async updateSelection(updatedSelection: Partial<TextSelection> & { id: string }): Promise<void> {
    return this.enqueueRequest('updateSelection', async () => {
      const index = this.state.selections.findIndex(s => s.id === updatedSelection.id)
      if (index !== -1) {
        this.state.selections[index] = { ...this.state.selections[index], ...updatedSelection }
        this.emit('selections', this.state.selections)
        console.log('🔄 Selection updated:', updatedSelection.id)
      }
    })
  }

  async updateSelectionInPlace(selectionId: string, updates: Partial<TextSelection>): Promise<void> {
    return this.enqueueRequest('updateSelectionInPlace', async () => {
      const index = this.state.selections.findIndex(s => s.id === selectionId)
      if (index !== -1) {
        this.state.selections[index] = { ...this.state.selections[index], ...updates }
        // Don't emit 'selections' event to avoid triggering full re-renders
        // Instead emit a more specific event for graceful updates
        this.emit('selectionUpdated', { selectionId, updates, selection: this.state.selections[index] })
        console.log('🔄 Selection updated in place (graceful):', selectionId)
      }
    })
  }

  // Enhanced document management with request queuing
  async setProjectDocuments(documents: ProjectDocument[]): Promise<void> {
    return this.enqueueRequest('setProjectDocuments', async () => {
      this.state.projectDocuments = documents
      this.emit('projectDocuments', documents)
      console.log('📂 Project documents updated:', documents.length)
    })
  }

  setDescriptionList(list: string | null): void {
    this.state.descriptionList = list
    this.emit('descriptionList', list)
    console.log('📋 Description list updated')
  }

  setCoordinates(coords: string[], skipPersistence: boolean = false): void {
    console.log('📍 Setting coordinates:', {
      newLength: coords.length,
      oldLength: this.state.coordinates.length,
      hasProject: !!this.state.currentProject,
      projectId: this.state.currentProject?.id,
      currentStep: this.state.currentStep,
      skipPersistence
    })
    
    this.state.coordinates = coords
    this.emit('coordinates', coords)
    
    // Only persist if not explicitly skipped (used when clearing for project switch)
    if (!skipPersistence && this.state.currentProject) {
      if (coords.length > 0) {
        localStorage.setItem(`coords_${this.state.currentProject.id}`, JSON.stringify(coords))
        console.log('💾 Persisted coordinates for project:', {
          projectId: this.state.currentProject.id,
          count: coords.length,
          storage: 'set'
        })
      } else {
        // If coordinates are empty, remove the stored value
        localStorage.removeItem(`coords_${this.state.currentProject.id}`)
        console.log('🗑️ Cleared stored coordinates for project:', {
          projectId: this.state.currentProject.id,
          storage: 'removed'
        })
      }
    } else if (skipPersistence) {
      console.log('⏭️ Skipped persistence for coordinate clearing')
    }
  }

  restoreCoordinates(projectId: string): void {
    console.log('📍 Attempting to restore coordinates for project:', projectId)
    try {
      const stored = localStorage.getItem(`coords_${projectId}`)
      console.log('📍 Found stored coordinates:', !!stored)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.state.coordinates = parsed
          this.emit('coordinates', parsed)
          console.log('📐 Restored coordinates:', {
            projectId,
            count: parsed.length,
            coordinates: parsed
          })
        } else {
          console.log('⚠️ Stored coordinates were empty or invalid:', parsed)
        }
      } else {
        console.log('⚠️ No stored coordinates found for project:', projectId)
      }
    } catch (e) {
      console.warn('❌ Failed to restore coordinates:', e)
    }
  }

  setHasFormattedText(hasFormattedText: boolean): void {
    this.state.hasFormattedText = hasFormattedText
    this.emit('hasFormattedText', hasFormattedText)
  }

  getAdvancedMode(): boolean {
    return this.state.advancedMode
  }

  setAdvancedMode(advancedMode: boolean): void {
    this.state.advancedMode = advancedMode
    this.emit('advancedMode', advancedMode)
    console.log('🔄 Advanced mode updated:', advancedMode)
  }

  // Clear all state
  reset(): void {
    // Save current project ID and coordinates before reset if we have them
    const oldProjectId = this.state.currentProject?.id
    const oldCoordinates = this.state.coordinates

    this.state = {
      currentProject: null,
      currentFile: null,
      currentStep: 1,
      selections: [],
      projectDocuments: [],
      isLoading: false,
      error: null,
      descriptionList: null,
      coordinates: [],
      hasFormattedText: false,
      advancedMode: true,
      ocrStatuses: new Map()
    }
    this.emit('currentProject', null)
    this.emit('currentFile', null)
    this.emit('currentStep', 1)
    this.emit('selections', [])
    this.emit('projectDocuments', [])
    this.emit('isLoading', false)
    this.emit('error', null)
    this.emit('descriptionList', null)
    this.emit('coordinates', [])

    // If we had a project, persist its coordinates before clearing
    if (oldProjectId && oldCoordinates.length > 0) {
      localStorage.setItem(`coords_${oldProjectId}`, JSON.stringify(oldCoordinates))
      console.log('💾 Persisted coordinates during reset:', {
        projectId: oldProjectId,
        count: oldCoordinates.length
      })
    }
    
    console.log('🔄 App state reset')
  }

  // PERSISTENCE: Load selections from database for current project/PDF
  async loadSelectionsFromDatabase(apiService: any): Promise<void> {
    return this.enqueueRequest('loadSelectionsFromDatabase', async () => {
      try {
        const currentProject = this.getCurrentProject()
        if (!currentProject) return

        const result = await apiService.listProjectSelections(currentProject.id)
        this.state.selections = result.selections || []
        this.emit('selections', this.state.selections)
        console.log(`💾 Loaded ${result.selections?.length || 0} selections from database`)
      } catch (error) {
        console.error('Failed to load selections from database:', error)
      }
    })
  }

  // Get selections for a specific PDF
  getSelectionsForDocument(documentId: string): TextSelection[] {
    return this.state.selections.filter(s => s.document_id === documentId)
  }

  // ============================================================================
  // OCR Status Management
  // ============================================================================

  updateOCRStatus(selectionId: string, status: any): void {
    this.state.ocrStatuses.set(selectionId, status)
    this.emit('ocrStatusChanged', { selectionId, status })
    console.log('🔄 OCR status updated for selection:', selectionId, status.status)
  }

  getOCRStatus(selectionId: string): any | undefined {
    return this.state.ocrStatuses.get(selectionId)
  }

  getAllOCRStatuses(): any[] {
    return Array.from(this.state.ocrStatuses.values())
  }

  clearOCRStatuses(): void {
    this.state.ocrStatuses.clear()
    console.log('🧹 OCR statuses cleared')
  }

  removeOCRStatus(selectionId: string): void {
    this.state.ocrStatuses.delete(selectionId)
    console.log('🗑️ OCR status removed for selection:', selectionId)
  }
}